import {
  Box,
  Chip,
  Stack,
  type Theme,
  Tooltip,
  styled,
  useTheme,
} from "@mui/material"
import { type ReactNode, useState } from "react"
import { Download, type Icon, Table } from "react-feather"
import { type FileDownloadOptions, useFileDownload } from "../../../hooks"
import { Dialog, ModalContentText } from "../../feedback"
import { SafeApiComponent } from "../../layout"

export type MultipleExportOptions = FileDownloadOptions & {
  label: string
  disabled?: boolean
  tooltip?: ReactNode
  Icon?: Icon
  iconColor?: (theme: Theme) => string
}

export type ExportModalProps = {
  open: boolean
  onClose: () => void
  exportsLabel?: string
  emptyExportsLabel?: string
  title?: ReactNode
  confirmButtonLabel?: string
  useExports: () => MultipleExportOptions[]
}

export const ExportModal = ({
  open,
  onClose,
  useExports,
  exportsLabel = "Choisir les informations que vous souhaitez exporter",
  emptyExportsLabel = "Aucune donnée d'export",
  title = "Export de données",
  confirmButtonLabel = "Exporter",
}: ExportModalProps) => {
  const [selectedExport, setSelectedExport] = useState<MultipleExportOptions | null>(
    null,
  )
  const { startDownload, isDownloading } = useFileDownload(
    selectedExport ?? { name: "", endpoint: "" },
  )
  const close = () => {
    setSelectedExport(null)
    onClose()
  }
  const validateAction = async () => {
    await startDownload()
    close()
  }

  return (
    <Dialog
      Icon={Download}
      title={title}
      validateText={confirmButtonLabel}
      cancelText="Annuler"
      open={open}
      onClose={close}
      validateAction={validateAction}
      validateEnabled={selectedExport !== null}
      cancelAction={close}
      loading={isDownloading}
    >
      <Stack gap={3}>
        <SafeApiComponent
          suspense={{ skeletonProps: { height: "60px", width: "100%" } }}
        >
          <ExportCards
            useExport={useExports}
            selectedExport={selectedExport}
            setSelectedExport={setSelectedExport}
            emptyExportsLabel={emptyExportsLabel}
            exportsLabel={exportsLabel}
          />
        </SafeApiComponent>
      </Stack>
    </Dialog>
  )
}

interface ExportCardsProps {
  exportsLabel: string
  emptyExportsLabel: string
  selectedExport: MultipleExportOptions | null
  setSelectedExport: (args: MultipleExportOptions) => void
}

const ExportCards = ({
  useExport,
  selectedExport,
  setSelectedExport,
  emptyExportsLabel,
  exportsLabel,
}: ExportCardsProps & {
  useExport: () => MultipleExportOptions[]
}) => {
  const exports = useExport()
  const theme = useTheme()
  if (exports.length === 0 || exports.every((e) => e.disabled)) {
    return <ModalContentText>{emptyExportsLabel}</ModalContentText>
  }

  return (
    <>
      <ModalContentText>{exportsLabel}</ModalContentText>
      <ExportContainer>
        {exports.map((exportOption) => {
          const ExportIcon = exportOption.Icon ?? Table
          const iconColor =
            exportOption.iconColor?.(theme) ?? theme.palette.secondary.main

          return (
            <Tooltip title={exportOption.tooltip} key={exportOption.name}>
              {/* Box required: the tooltip does not show when its direct child is disabled */}
              <Box>
                <ExportChoice
                  icon={<ExportIcon size="28px" color={iconColor} />}
                  label={exportOption.label}
                  variant={
                    selectedExport?.name === exportOption.name
                      ? "filled"
                      : "outlined"
                  }
                  onClick={() => setSelectedExport(exportOption)}
                  disabled={exportOption.disabled}
                />
              </Box>
            </Tooltip>
          )
        })}
      </ExportContainer>
    </>
  )
}

const ExportContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(2),
  maxWidth: "100%",
  flexWrap: "wrap",
}))

const ExportChoice = styled(Chip)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  minWidth: "280px",
  height: "71px",
  fontSize: theme.typography.pxToRem(16),
}))
